'use client';

import { ChatDraftIndicator } from '@/components/chat/components/ChatDraftIndicator';
import { cn } from '@/lib/cn';
import type { ChatListItem } from '@/types';
import { isBackendPlaceholderChatTitle } from '@/utils/chatPlaceholderTitle';
import { Button } from '@heidi/design-system/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@heidi/design-system/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Input } from '@heidi/design-system/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyP2 } from '@heidi/design-system/typography';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useRenameInputFocus } from '@heidi/utils';
import { MoreHorizontal, Pencil, Pin, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

const noop = () => {};

interface EvidenceChatItemProps {
  chat: ChatListItem;
  section: 'saved' | 'recent' | 'older';
  isSelected: boolean;
  isStreaming: boolean;
  isSaved: boolean;
  showSaveButton?: boolean;
  currentChatId: string | null;
  onSelect: (chatId: string) => void;
  onSave: (e: React.MouseEvent, chatId: string) => void;
  onUnsave: (
    e: React.MouseEvent,
    chatId: string,
    source: 'sidebar' | 'saved_section'
  ) => void;
  onDelete: (e: React.MouseEvent, chat: ChatListItem) => void;
  /** When true, show inline input for renaming. */
  isEditing?: boolean;
  /** Current title value when editing (controlled by parent). */
  editingTitle?: string;
  /** Called when the user changes the title while editing. */
  onEditingTitleChange?: (value: string) => void;
  /** Called when the user triggers rename (e.g. from dropdown or context menu). */
  onRenameRequest?: () => void;
  /** Called when the user confirms the new title (blur or Enter). */
  onRenameSubmit?: (chatId: string) => void;
  /** Called when the user cancels rename (Escape). */
  onRenameCancel?: () => void;
}

export function EvidenceChatItem({
  chat,
  section,
  isSelected,
  isStreaming,
  isSaved,
  showSaveButton = true,
  currentChatId,
  onSelect,
  onSave,
  onUnsave,
  onDelete,
  isEditing = false,
  editingTitle = '',
  onEditingTitleChange,
  onRenameRequest,
  onRenameSubmit,
  onRenameCancel,
}: EvidenceChatItemProps) {
  const intl = useIntl();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const isMenuOpen = dropdownOpen || contextOpen;
  const isAwaitingTitle = isStreaming && isBackendPlaceholderChatTitle(chat.title);
  // Backend title is an untranslated literal used for match-on-default logic
  // elsewhere (see deriveOptimisticChatTitle in useMutateCreateChat.ts) — swap
  // it for the translated placeholder only at render time.
  const displayChatTitle = isBackendPlaceholderChatTitle(chat.title)
    ? intl.formatMessage({
        defaultMessage: 'New chat',
        id: 'ex6NWuIWQJ',
        description: 'Placeholder title shown for a chat that has not been named yet',
      })
    : chat.title;

  const { inputRef: renameInputRef, armedRef: renameArmedRef } = useRenameInputFocus({
    isEditing,
    onSubmit: onRenameSubmit ?? noop,
    chatId: chat.id,
  });

  const contextMenuSaveLabel = isSaved
    ? intl.formatMessage({
        defaultMessage: 'Unpin',
        id: 'vZVIaHyk7r',
        description: 'Evidence chat item: remove the chat from pinned chats',
      })
    : intl.formatMessage({
        defaultMessage: 'Pin',
        id: 'j88x4lIH6e',
        description: 'Evidence chat item: pin the chat',
      });
  const renameLabel = intl.formatMessage({
    defaultMessage: 'Rename',
    id: 'T4ZKaenAKC',
    description: 'Evidence chat item: rename the chat',
  });
  const moreActionsLabel = intl.formatMessage({
    defaultMessage: 'More actions',
    id: 'KNz4gLFSPZ',
    description: 'Evidence chat item: open the row actions menu',
  });
  const deleteLabel = intl.formatMessage({
    defaultMessage: 'Delete',
    id: 'mhalGmrC8t',
    description: 'Evidence chat item: delete the chat',
  });
  const handleContextSave = useCallback(
    (e: React.MouseEvent) => {
      if (isSaved) {
        onUnsave(e, chat.id, section === 'saved' ? 'saved_section' : 'sidebar');
      } else {
        onSave(e, chat.id);
      }
    },
    [isSaved, onUnsave, onSave, chat.id, section]
  );

  return (
    <ContextMenu onOpenChange={(open) => setContextOpen(open)}>
      <ContextMenuTrigger asChild>
        <Button
          size="lg"
          variant="outline"
          onClick={() => !isEditing && onSelect(chat.id)}
          data-menu-open={isMenuOpen ? 'true' : undefined}
          className={cn(
            'group/chat-item relative h-auto w-full justify-between rounded-lg p-3 text-left',
            // Override active background to prevent flash while dropdown/context menu is open.
            // The base button variant uses a different active colour which conflicts.
            isSelected
              ? 'bg-background-secondary active:!bg-background-secondary'
              : 'bg-surface hover:bg-background-tertiary active:!bg-background-tertiary',
            !isSelected && isMenuOpen && 'bg-background-tertiary'
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {isEditing ? (
              <Input
                ref={renameInputRef}
                value={editingTitle}
                onChange={(e) => onEditingTitleChange?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (renameArmedRef.current) {
                      onRenameSubmit?.(chat.id);
                    }
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    onRenameCancel?.();
                  }
                  e.stopPropagation();
                }}
                onClick={(e) => e.stopPropagation()}
                wrapperClassName="min-w-0 flex-1 border-0 bg-transparent px-0 focus-within:border-0"
                className="h-auto font-normal leading-normal tracking-normal"
                aria-label={renameLabel}
              />
            ) : (
              <TypographyP2 className="truncate">{displayChatTitle}</TypographyP2>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ChatDraftIndicator chatId={chat.id} currentChatId={currentChatId} />
            {showSaveButton && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={contextMenuSaveLabel}
                      className="size-auto shrink-0 p-1 text-text-secondary opacity-0 hover:text-text-primary focus-visible:opacity-100 group-hover/chat-item:opacity-100 group-data-[menu-open=true]/chat-item:opacity-100"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSaved) {
                          onUnsave(
                            e,
                            chat.id,
                            section === 'saved' ? 'saved_section' : 'sidebar'
                          );
                        } else {
                          onSave(e, chat.id);
                        }
                      }}
                    >
                      <Pin size={16} className={cn(isSaved && 'fill-current')} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    {contextMenuSaveLabel}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={moreActionsLabel}
                  className="size-auto shrink-0 p-1 text-text-secondary opacity-0 hover:text-text-primary focus-visible:opacity-100 group-hover/chat-item:opacity-100 group-data-[menu-open=true]/chat-item:opacity-100"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  disabled={isAwaitingTitle}
                  onSelect={(e) => {
                    e.preventDefault();
                    setDropdownOpen(false);
                    onRenameRequest?.();
                  }}
                >
                  <Pencil size={16} />
                  {renameLabel}
                </DropdownMenuItem>
                {!isSaved && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(e, chat);
                    }}
                    variant="destructive"
                  >
                    <Trash2 size={16} />
                    {deleteLabel}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {isStreaming && (
              <div className="shrink-0" aria-label="Chat is thinking">
                <Loader size={14} strokeWidth={2} />
              </div>
            )}
          </div>
        </Button>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {showSaveButton && (
          <ContextMenuItem onClick={handleContextSave}>
            <Pin className={cn(isSaved && 'fill-current')} />
            {contextMenuSaveLabel}
          </ContextMenuItem>
        )}
        <ContextMenuItem
          disabled={isAwaitingTitle}
          onSelect={() => {
            setContextOpen(false);
            onRenameRequest?.();
          }}
        >
          <Pencil />
          {renameLabel}
        </ContextMenuItem>
        {!isSaved && (
          <ContextMenuItem variant="destructive" onClick={(e) => onDelete(e, chat)}>
            <Trash2 />
            {deleteLabel}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
